import { useEffect, useState, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { coreV1GetDataSource } from '@antimatterhq/asl-api-ts-v1';
import { useMutation } from '@antimatterhq/ui/hooks/mutations/useMutation';
import { Input, InputProps } from '@antimatterhq/ui/components/input';
import { Icon } from '@antimatterhq/ui/components/icon';
import { useField, useFormikContext } from 'formik';
import { cn } from '@/lib/utils';
import { useCheckFormikError } from '@/hooks/formik/useCheckFormikError';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAsyncValidation } from '@/contexts/AsyncValidationContext';
import { defaultOptions } from '@/utils/query';

export interface UseDataSourceNameValidationProps {
  value: string;
  onError?: (error: string | null) => void;
  debounceTime?: number;
  disabled?: boolean;
}

const DEFAULT_DEBOUNCE_TIME = 500;

export const useValidateDatasourceName = () => {
  const { workspace } = useWorkspace();

  return useMutation({
    mutationFn: async (name: string) => {
      const result = await coreV1GetDataSource({
        ...defaultOptions(),
        path: { workspace, name },
      });

      if (result.data) {
        throw new Error('A data source with this name already exists');
      }

      return undefined;
    },
    retry: false,
  });
};

export function useDataSourceNameValidation({
  value,
  onError,
  debounceTime = DEFAULT_DEBOUNCE_TIME,
  disabled,
}: UseDataSourceNameValidationProps) {
  const [isPending, setIsPending] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: validateDatasourceName } = useValidateDatasourceName();

  const validateName = useCallback(
    async (inputValue: string) => {
      if (disabled) {
        setIsPending(false);
        setIsValid(null);
        setError(null);
        return;
      }

      if (!inputValue) {
        setIsPending(false);
        setIsValid(null);
        setError(null);
        return;
      }

      setIsPending(true);
      setIsValid(null);
      setError(null);

      await validateDatasourceName(inputValue, {
        onSuccess: async () => {
          // Validation succeeded (datasource DOES NOT exist)
          setIsValid(true);
          onError?.(null);
          setError(null);
        },
        onError: async ({ error: err }) => {
          // Validation failed (datasource exists or network error)
          setIsValid(false);
          const message = err?.message || 'Error validating data source name';
          setError(message);
          onError?.(message);
        },
      });

      // Ensure the pending state is cleared once the request is settled
      setIsPending(false);
    },
    [disabled, validateDatasourceName, onError],
  );

  const debouncedValidateName = useDebouncedCallback(
    validateName,
    debounceTime,
  );

  useEffect(() => {
    debouncedValidateName(value);

    return () => {
      debouncedValidateName.cancel();
    };
    // We want to re-run the debounced validation when the value changes
    // and when the disabled state changes
  }, [value, debouncedValidateName, disabled]);

  return { isPending, isValid, error };
}

interface FormDataSourceNameValidatedInputProps
  extends Omit<
    InputProps,
    'value' | 'onChange' | 'onBlur' | 'name' | 'variant'
  > {
  name: string;
  debounceTime?: number;
}

export const FormDataSourceNameValidatedInput = ({
  name,
  debounceTime,
  className,
  disabled,
  ...rest
}: FormDataSourceNameValidatedInputProps) => {
  const [field] = useField<string>(name);
  const { isSubmitting, setErrors } = useFormikContext();
  const { error: formikError } = useCheckFormikError({ name });
  const { setError, clearError, errors: asyncErrors } = useAsyncValidation();

  const {
    isPending,
    isValid,
    error: validationError,
  } = useDataSourceNameValidation({
    disabled,
    value: field.value,
    debounceTime,
    onError: useCallback(
      (error: string | null) => {
        // Set in Formik for immediate visual feedback
        setErrors({ [name]: error });

        // Also store in AsyncValidationContext to persist through blur
        if (error) {
          setError(name, error);
        } else {
          clearError(name);
        }
      },
      [name, setErrors, setError, clearError],
    ),
  });

  const asyncError = asyncErrors[name];
  const hasError =
    !!formikError ||
    !!asyncError ||
    (validationError !== null && !isPending) ||
    (isValid === false && !isPending);
  const isEffectivelyDisabled = disabled || isSubmitting;

  const variant = hasError ? 'error' : 'default';

  return (
    <div className="relative w-full max-w-form">
      <Input
        variant={variant}
        disabled={isEffectivelyDisabled}
        className={cn('max-w-form pr-10', className)}
        autoComplete="off"
        data-testid={`form-datasource-name-validated-input-${name}`}
        {...field}
        {...rest}
      />
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {isPending && (
          <Icon
            icon="lucide:loader-circle"
            className="h-4 w-4 animate-spin text-gray-400"
          />
        )}
        {isValid === true && !isPending && !formikError && (
          <Icon
            icon="lucide:check-circle"
            className={cn(
              'h-4 w-4 text-green-500',
              isEffectivelyDisabled ? 'opacity-50' : '',
            )}
          />
        )}
        {hasError && !isPending && (
          <Icon
            icon="lucide:octagon-alert"
            className={cn(
              'h-4 w-4',
              'text-red-500',
              isEffectivelyDisabled ? 'opacity-50' : '',
            )}
          />
        )}
      </div>
    </div>
  );
};
